import type {
  ID,
  LibraryHydratePayload,
  LibraryItem,
  LibraryKind,
  LibrarySymbolData,
} from '@octopus-do/octopus-project-core'
import axios from 'axios'

import { syncClient, syncProjectClient } from './sync'

// The workspace library lives in octopus-sync next to the project document.

interface ItemsResponse {
  items: LibraryItem<LibraryKind>[]
}

interface ItemResponse {
  item: LibraryItem<LibraryKind>
}

interface LibraryItemPatch {
  data: LibrarySymbolData
  position: number
}

interface WithVersion {
  version: number
}

/** What PATCH takes: any part of the item, always with the version it was read at. */
type UpdateLibraryItemBody = Partial<LibraryItemPatch> & WithVersion

export const listLibraryItems = async (
  workspaceId: string,
  kind: LibraryKind
): Promise<LibraryItem<LibraryKind>[]> => {
  const { data } = await syncClient().get<ItemsResponse>(
    `/workspaces/${workspaceId}/library/${kind}`
  )

  return data.items
}

export const createLibraryItem = async (
  workspaceId: string,
  kind: LibraryKind,
  itemData: LibrarySymbolData
): Promise<LibraryItem<LibraryKind>> => {
  const { data } = await syncClient().post<ItemResponse>(
    `/workspaces/${workspaceId}/library/${kind}`,
    { data: itemData }
  )

  return data.item
}

export const updateLibraryItem = async (
  workspaceId: string,
  kind: LibraryKind,
  id: ID,
  body: UpdateLibraryItemBody
): Promise<LibraryItem<LibraryKind>> => {
  const { data } = await syncClient().patch<ItemResponse>(
    `/workspaces/${workspaceId}/library/${kind}/${id}`,
    body
  )

  return data.item
}

export const deleteLibraryItem = async (
  workspaceId: string,
  kind: LibraryKind,
  id: ID
): Promise<void> => {
  await syncClient().delete(`/workspaces/${workspaceId}/library/${kind}/${id}`)
}

/** The items the refs of a project point at, readable with the project access. */
export const getProjectLibrary = async (
  projectId: ID
): Promise<LibraryHydratePayload> => {
  const { data } = await syncProjectClient(
    projectId
  ).get<LibraryHydratePayload>(`/projects/${projectId}/library`)

  return data
}

export const isConflictError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 409
